if (!customElements.get('screen-protector-upsell')) {
  customElements.define(
    'screen-protector-upsell',
    class ScreenProtectorUpsell extends HTMLElement {
      constructor() {
        super();
        this.variants = [];
        this.currentProtectorVariant = null;
        this.variantChangeUnsubscriber = null;
        this.onDocumentChange = this.onDocumentChange.bind(this);
        this.onAdd = this.onAdd.bind(this);
      }

      connectedCallback() {
        const variantsNode = this.querySelector('[data-cc-screen-variants]');
        if (!variantsNode) return;

        try {
          this.variants = JSON.parse(variantsNode.textContent || '[]');
        } catch (error) {
          console.error('Case & Charm screen protector variants could not be read.', error);
          return;
        }

        this.modelOptionIndex = Number.parseInt(this.dataset.modelOptionIndex || '0', 10) || 0;
        this.addButton = this.querySelector('[data-cc-screen-add]');
        this.buttonLabel = this.querySelector('[data-cc-screen-button-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.modelNode = this.querySelector('[data-cc-screen-model]');
        this.priceNode = this.querySelector('[data-cc-screen-price]');
        this.imageNode = this.querySelector('.cc-screen-upsell__image');
        this.errorNode = this.querySelector('[data-cc-screen-error]');

        this.addButton?.addEventListener('click', this.onAdd);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (String(event?.data?.sectionId) !== String(this.dataset.sectionId)) return;
            const model = event?.data?.variant?.options?.[this.modelOptionIndex];
            this.updateForModel(model);
          });
        }

        document.addEventListener('change', this.onDocumentChange);
        this.updateForModel(this.dataset.initialModel);
      }

      disconnectedCallback() {
        this.addButton?.removeEventListener('click', this.onAdd);
        document.removeEventListener('change', this.onDocumentChange);
        this.variantChangeUnsubscriber?.();
      }

      onDocumentChange(event) {
        const picker = event.target?.closest?.(`variant-selects[data-section="${this.dataset.sectionId}"]`);
        if (!picker) return;

        window.setTimeout(() => {
          const selectedVariantNode = picker.querySelector('[data-selected-variant]');
          if (!selectedVariantNode) return;
          try {
            const variant = JSON.parse(selectedVariantNode.textContent || 'null');
            const model = variant?.options?.[this.modelOptionIndex];
            if (model) this.updateForModel(model);
          } catch (_) {
            // The theme's variant-change event remains the primary update path.
          }
        }, 0);
      }

      normaliseModel(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/[®™]/g, '')
          .replace(/\bapple\b/g, '')
          .replace(/\bsamsung\b/g, '')
          .replace(/\bgalaxy\b/g, '')
          .replace(/[–—]/g, '-')
          .replace(/\+/g, ' plus ')
          .replace(/\s*\/\s*/g, '/')
          .replace(/\/iphone\s+/g, '/')
          .replace(/\s+/g, ' ')
          .trim();
      }

      targetModelKey(value) {
        const key = this.normaliseModel(value);

        const combinedModels = {
          'iphone 14': 'iphone 14/13/13 pro',
          'iphone 13': 'iphone 14/13/13 pro',
          'iphone 13 pro': 'iphone 14/13/13 pro',
          'iphone 14 plus': 'iphone 14 plus/13 pro max',
          'iphone 13 pro max': 'iphone 14 plus/13 pro max',
          'iphone 12': 'iphone 12/12 pro',
          'iphone 12 pro': 'iphone 12/12 pro',
        };

        if (combinedModels[key]) return combinedModels[key];

        const supported = new Set([
          'iphone 17',
          'iphone 17 pro',
          'iphone 17 pro max',
          'iphone 17 air',
          'iphone 17e',
          'iphone 16',
          'iphone 16 pro',
          'iphone 16 pro max',
          'iphone 16 plus',
          'iphone 16e',
          'iphone 15',
          'iphone 15 pro',
          'iphone 15 pro max',
          'iphone 15 plus',
          'iphone 14 pro',
          'iphone 14 pro max',
          'iphone 14 plus/13 pro max',
          'iphone 14/13/13 pro',
          'iphone 12/12 pro',
          'iphone 12 pro max',
          's26',
          's26 plus',
          's26 ultra',
          's25',
          's25 plus',
          's25 ultra',
          's24',
          's24 plus',
          's24 ultra',
          's23',
          's23 plus',
          's23 ultra',
        ]);

        return supported.has(key) ? key : null;
      }

      variantModelKey(variant) {
        return this.targetModelKey(variant?.model || variant?.title);
      }

      updateForModel(model) {
        const targetKey = this.targetModelKey(model);
        if (!targetKey) {
          this.hideUpsell();
          return;
        }

        const protectorVariant = this.variants.find((variant) => this.variantModelKey(variant) === targetKey);
        if (!protectorVariant) {
          this.hideUpsell();
          return;
        }

        this.currentProtectorVariant = protectorVariant;
        this.hidden = false;

        if (this.modelNode) this.modelNode.textContent = model;
        if (this.priceNode) this.priceNode.textContent = protectorVariant.price || '';
        if (this.imageNode && protectorVariant.image) this.imageNode.src = protectorVariant.image;

        if (this.addButton) {
          this.addButton.disabled = !protectorVariant.available;
          this.addButton.setAttribute('aria-disabled', protectorVariant.available ? 'false' : 'true');
        }
        if (this.buttonLabel) this.buttonLabel.textContent = protectorVariant.available ? '+ Add to basket' : 'Sold out';
        this.clearError();
      }

      hideUpsell() {
        this.currentProtectorVariant = null;
        this.hidden = true;
        this.clearError();
      }

      async onAdd() {
        const variant = this.currentProtectorVariant;
        if (!variant?.id || !variant.available || !this.addButton) return;
        if (this.addButton.getAttribute('aria-disabled') === 'true') return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.addButton.setAttribute('aria-disabled', 'true');
        this.addButton.disabled = true;
        this.addButton.classList.add('loading');
        this.spinner?.classList.remove('hidden');
        if (this.buttonLabel) this.buttonLabel.textContent = 'Adding…';
        this.clearError();

        try {
          const config = typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/json' } };

          config.headers = config.headers || {};
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', String(variant.id));
          formData.append('quantity', '1');

          if (cart?.getSectionsToRender) {
            formData.append(
              'sections',
              cart
                .getSectionsToRender()
                .map((section) => section.id)
                .join(',')
            );
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement?.(document.activeElement);
          }

          config.body = formData;
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'This screen protector could not be added to your basket.');
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'case-charm-screen-protector-upsell',
              productVariantId: String(variant.id),
              cartData: data,
            });
          }

          if (cart?.renderContents && data.sections) {
            cart.renderContents(data);
          } else if (!cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          if (this.buttonLabel) this.buttonLabel.textContent = 'Added ✓';
          window.setTimeout(() => {
            if (this.buttonLabel && this.currentProtectorVariant?.id === variant.id) {
              this.buttonLabel.textContent = '+ Add to basket';
            }
          }, 1800);
        } catch (error) {
          this.showError(error?.message || 'This screen protector could not be added to your basket.');
          if (this.buttonLabel) this.buttonLabel.textContent = '+ Add to basket';
        } finally {
          this.addButton.classList.remove('loading');
          this.spinner?.classList.add('hidden');
          const stillAvailable = Boolean(this.currentProtectorVariant?.available);
          this.addButton.disabled = !stillAvailable;
          this.addButton.setAttribute('aria-disabled', stillAvailable ? 'false' : 'true');
        }
      }

      showError(message) {
        if (!this.errorNode) return;
        this.errorNode.textContent = message;
        this.errorNode.hidden = false;
      }

      clearError() {
        if (!this.errorNode) return;
        this.errorNode.textContent = '';
        this.errorNode.hidden = true;
      }
    }
  );
}
